/**
 * SHELL — local persistence (localStorage), the offline/anonymous UX cache.
 *
 * IMPORTANT: the streak SOURCE OF TRUTH is the Backend DB. This cache only
 * powers anonymous-first and offline display so the player always sees their
 * loss-aversion hook even with no network. Backend's value overrides ours on
 * every successful submit/leaderboard read.
 *
 * All reads are defensive (corrupt JSON, private-mode throws) and never throw to
 * callers — a dead storage degrades to "no cache", not a crash.
 */

import type { DateKey } from '@/types';
import type { ScoreMetrics } from '@/types';

const NS = 'dailyroll:';
const K = {
  streak: `${NS}streak`,
  lastResult: `${NS}lastResult`,
  settings: `${NS}settings`,
  playedDays: `${NS}playedDays`,
  installDismissed: `${NS}installDismissed`,
  notifyPrompted: `${NS}notifyPrompted`,
} as const;

/** Dev-tools flag key (separate, short namespace so it's easy to set by hand). */
const DEV_FLAG_KEY = 'dr.dev';

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage full or unavailable — display-only cache, safe to drop */
  }
}

function readJson<T>(key: string, fallback: T): T {
  const raw = safeGet(key);
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/* ---- Streak cache --------------------------------------------------------- */

export interface StreakCache {
  /** Current consecutive-day streak. */
  readonly current: number;
  /** Best streak ever reached. */
  readonly best: number;
  /** The last UTC day the player completed a run. */
  readonly lastPlayedDay: DateKey | null;
}

const EMPTY_STREAK: StreakCache = { current: 0, best: 0, lastPlayedDay: null };

export function getStreak(): StreakCache {
  return readJson<StreakCache>(K.streak, EMPTY_STREAK);
}

/** Overwrite the cache with the server-authoritative streak. */
export function setServerStreak(current: number, day: DateKey): void {
  const prev = getStreak();
  const next: StreakCache = {
    current,
    best: Math.max(prev.best, current),
    lastPlayedDay: day,
  };
  safeSet(K.streak, JSON.stringify(next));
}

/**
 * Optimistically advance the local streak after finishing a run offline.
 * Returns the new cache. Idempotent for the same day. Server overrides later.
 */
export function bumpLocalStreak(day: DateKey): StreakCache {
  const prev = getStreak();
  if (prev.lastPlayedDay === day) return prev; // already counted today
  const isConsecutive = prev.lastPlayedDay !== null && isPreviousUtcDay(prev.lastPlayedDay, day);
  const current = isConsecutive ? prev.current + 1 : 1;
  const next: StreakCache = { current, best: Math.max(prev.best, current), lastPlayedDay: day };
  safeSet(K.streak, JSON.stringify(next));
  return next;
}

/** True if `prev` is exactly one UTC day before `day`. */
export function isPreviousUtcDay(prev: DateKey, day: DateKey): boolean {
  const pd = Date.parse(`${prev}T00:00:00Z`);
  const dd = Date.parse(`${day}T00:00:00Z`);
  if (Number.isNaN(pd) || Number.isNaN(dd)) return false;
  return dd - pd === 86_400_000;
}

/**
 * Is the player's streak at risk of breaking? True when they have an active
 * streak but have NOT played the given (today's) day yet.
 */
export function isStreakAtRisk(today: DateKey, streak: StreakCache = getStreak()): boolean {
  if (streak.current <= 0 || streak.lastPlayedDay === null) return false;
  if (streak.lastPlayedDay === today) return false; // already safe today
  // Still alive only if last play was yesterday; otherwise it's already broken.
  return isPreviousUtcDay(streak.lastPlayedDay, today);
}

/** Whether the player already completed today's run (per local cache). */
export function hasPlayedToday(today: DateKey, streak: StreakCache = getStreak()): boolean {
  return streak.lastPlayedDay === today;
}

/* ---- Last result (for the results/share screens after refresh) ------------ */

export interface StoredResult {
  readonly dateKey: DateKey;
  readonly metrics: ScoreMetrics;
  readonly rankGlobal?: number;
  readonly percentile?: number;
  readonly submitted: boolean;
}

export function getLastResult(): StoredResult | null {
  return readJson<StoredResult | null>(K.lastResult, null);
}

export function setLastResult(result: StoredResult): void {
  safeSet(K.lastResult, JSON.stringify(result));
}

/* ---- Settings ------------------------------------------------------------- */

export interface Settings {
  readonly reducedMotion: boolean;
  readonly muted: boolean;
  readonly dailyReminder: boolean;
}

const DEFAULT_SETTINGS: Settings = { reducedMotion: false, muted: false, dailyReminder: false };

export function getSettings(): Settings {
  return { ...DEFAULT_SETTINGS, ...readJson<Partial<Settings>>(K.settings, {}) };
}

export function setSettings(patch: Partial<Settings>): Settings {
  const next = { ...getSettings(), ...patch };
  safeSet(K.settings, JSON.stringify(next));
  return next;
}

/* ---- One-shot UI flags ---------------------------------------------------- */

export function isInstallDismissed(): boolean {
  return safeGet(K.installDismissed) === '1';
}
export function dismissInstall(): void {
  safeSet(K.installDismissed, '1');
}

export function wasNotifyPrompted(): boolean {
  return safeGet(K.notifyPrompted) === '1';
}
export function markNotifyPrompted(): void {
  safeSet(K.notifyPrompted, '1');
}

/* ---- Dev tools (playtest only) -------------------------------------------- */

/**
 * Is the local dev-tools flag active? True when `dr.dev === '1'` is in
 * localStorage. The `?dev` URL param is handled by the caller (it persists the
 * flag here so it survives navigation); this is the storage-side check.
 */
export function isDevFlagEnabled(): boolean {
  return safeGet(DEV_FLAG_KEY) === '1';
}

/** Persist the dev-tools flag so it survives navigation (set when `?dev` seen). */
export function enableDevFlag(): void {
  safeSet(DEV_FLAG_KEY, '1');
}

/**
 * DEV ONLY — clear the local one-attempt-per-day lock for `today` so the SCORED
 * daily run can be replayed during playtesting.
 *
 * What it resets locally:
 *   - the daily-lock / "played today" mark: `StreakCache.lastPlayedDay` is rolled
 *     back if it points at `today` (so `hasPlayedToday(today)` becomes false).
 *     We do NOT touch `current`/`best` here — playtesters keep their streak number.
 *   - today's cached `lastResult` (the stored completed-run result for `today`).
 *
 * NOTE: this only unlocks the run LOCALLY. The server still enforces
 * one-submit-per-day — a second submit for the same day returns `duplicate`.
 * That's fine for the playtest need: we just want to re-drive the run.
 */
export function clearTodaysPlay(today: DateKey): void {
  const streak = getStreak();
  if (streak.lastPlayedDay === today) {
    const next: StreakCache = { ...streak, lastPlayedDay: null };
    safeSet(K.streak, JSON.stringify(next));
  }
  const last = getLastResult();
  if (last?.dateKey === today) {
    safeRemove(K.lastResult);
  }
}
